import React, { useState, useContext, useEffect, useCallback }  from 'react';
import { observer } from 'mobx-react-lite';
import { RootStoreContext } from 'stores/root_store';
// import { AuthContext } from '../contexts/auth_context';

import { Identity } from 'stores/identity_store';
//import  { SocketContext, SocketContextType } from '../contexts/socketio_context';
import useAuth from 'hooks/useAuth';
import { forageGet, forageSet } from 'utils/misc';

type UserIdentityContextType = {
    loadingIdentities: boolean
    identities: Identity[]
    currentIdentity: Identity
    setCurrentIdentity: (d: Identity) => (void)
    verifiedIdentity: Identity
    setVerifiedIdentity?: (d: Identity) => (void)
    updateIdentity: (identity: Identity) => Promise<Identity | undefined>
    updateRole: (identityKey: string, role: string) => Promise<Identity | undefined>
    createIdentity: (identity: Identity) => Promise<Identity | undefined>
    isYou: (s: string) => (boolean),
}


// const AuthContext = React.createContext<AuthContextType>({
//     currentUser: {} as UserProps,
//     token: ""
// });


const UserIdentityContext = React.createContext<UserIdentityContextType>({
    loadingIdentities: true,
    identities: [] as Identity[],
    currentIdentity: {} as Identity,
    setCurrentIdentity: () => { throw new Error("not implemented") },
    verifiedIdentity: {} as Identity,
    updateIdentity: () => { throw new Error("not implemented") },
    updateRole: () => { throw new Error("not implemented") },
    createIdentity: () => { throw new Error("not implemented") },
    isYou: () => { throw new Error("not implemented") },
});


const UserIdentityProvider = observer(({ children }: { children: React.ReactNode  })=> {

    const { currentUser } = useAuth();
    const [identities, setIdentities] = useState([] as Identity[]);
    const [currentIdentity, setCurrentIdentityState] = useState({} as Identity);
    const [verifiedIdentity, updateVerifiedIdentity] = useState({} as Identity);

    //    const [defaultIdentity, setDefaultIdentity] = useState({} as Identity);
    const [loading, setLoading] = useState(true);

    const [announced, setAnnounced] = useState(false);
    const { userIdentityStore, identityStore } = useContext(RootStoreContext);

    // const { socketId, socketEmit } = useContext(SocketContext) as SocketContextType;

    // This is called from IdentityFrame, we u updateVerifiedIdentity() in this file
    function setVerifiedIdentity(d: Identity) {
        updateVerifiedIdentity(d);
        identityStore.updateItem(d);
    }

    async function createIdentity(i: Identity) {
        return userIdentityStore.createIdentity(i).then((i) => {
            if(i) {
                if(!currentIdentity.uuid) {
                    setCurrentIdentity(i);
                }
            }
            return i;
        });
    }

    const setCurrentIdentity = useCallback((d: Identity) => {
        setCurrentIdentityState(d);
        userIdentityStore.currentIdentityKey = d.uuid;
        forageSet("currentIdentity", JSON.stringify(d));
    }, [setCurrentIdentityState, userIdentityStore]);

    useEffect(() => {
        if(loading) {
            if(currentUser?.uuid) {
            setLoading(false);
            userIdentityStore.fetchUserIdentities().
                then(() => {
                    const ids = userIdentityStore.getUserIdentities()
                    setIdentities(ids);
                    const verifiedIdentities = ids.filter((i) => i.verified != 'no');
                    if(verifiedIdentities.length) {
                        updateVerifiedIdentity(verifiedIdentities[0]);
                    }

                    forageGet<Identity|undefined>("currentIdentity").then((identity) => {
                        // if identity is string, parse it
                        if(typeof identity == "string") {
                            identity = JSON.parse(identity);
                        }
                        // if they've set their identity, use that
                        if(identity) {
                            // Grab the identity from the store
                            ids.forEach((i) => {
                                if(i.uuid == identity.uuid) {
                                    setCurrentIdentity(i);
                                }
                            });
                        }
                        // otherwise, use the last one i guess?
                        else {
                            if (ids.length) {
                                setCurrentIdentity(ids[ids.length - 1]);
                            }
                        }
                    });
                });
            }
        }
    }, [userIdentityStore, loading, setCurrentIdentity, currentUser?.uuid]);


    useEffect(() => {
        if(!announced) {
            setAnnounced(true);
            userIdentityStore.presenceStart();
            return () => {
                userIdentityStore.presenceStop();
            }
        }
    }, [announced, setAnnounced, userIdentityStore]);


    function isYou(identityKey: string) {
        // Compare by both key and uuid to handle both formats
        return (currentIdentity.uuid == identityKey || currentIdentity.key == identityKey) ? true : false;
    }

    function updateIdentity(identity: Identity) {
        console.log("updating identity");
        return userIdentityStore.updateIdentity(identity);
    }

    function updateRole(identityKey: string, role: string) {
        return userIdentityStore.updateRole(identityKey, role);
    }


    const data = {
        loadingIdentities: loading,
        identities,
        currentIdentity,
        verifiedIdentity,
        setVerifiedIdentity,
        setCurrentIdentity,
        isYou,
        updateIdentity: updateIdentity,
        updateRole: updateRole,
        createIdentity
        //        defaultIdentity
    }
    return (
        <UserIdentityContext.Provider value={data}>{ children }</UserIdentityContext.Provider>
    )


});

export { UserIdentityContext, UserIdentityProvider };
